import { readItems, readMe } from "@directus/sdk";
import { directus } from "@/lib/directus";
import {
  Indicadores,
  IndicadoresRaw,
  LegendFormatted,
  LegendRaw,
} from "@/types/types";
import { fmt } from "./common";

export const fetchIndicadores = async (tenant_id: number) => {
  try {
    const user = await directus.request(readMe());
    const lang = user.title;
    const result = await directus.request<IndicadoresRaw[]>(
      readItems("indicadores_urbanos", {
        fields: [
          "*",
          "dimension.*",
          "dimension.translations.*",
          "translations.*",
          "valores.*",
        ],
      })
    );
    const filtered = result.map((indicador) => ({
      ...indicador,
      valores: (indicador.valores ?? []).filter((v) => v.tenant === tenant_id),
    }));

    const indicadores: Indicadores[] = filtered.map((item) => {
      const translation =
        item.translations.find((t) => t.lenguajes_code === lang) ||
        item.translations[0];
      const translation_dimension =
        item.dimension.translations.find((t) => t.lenguajes_code === lang) ||
        item.dimension.translations[0];
      const hasValores = Array.isArray(item.valores) && item.valores.length > 0;
      const valor = hasValores ? item.valores[0].valor : 0;

      return {
        id: item.id,
        nombre: translation.nombre ?? "",
        nombre_campo_json: item.nombre_campo_json ?? "",
        descripcion: translation.descripcion ?? "",
        calculo: translation.calculo ?? "",
        valor,
        dimension_formatted: item.dimension?.id ?? undefined,
        dimension_element: {
          id: item.dimension?.id ?? 0,
          color: item.dimension?.color ?? "",
          nombre: translation_dimension.nombre ?? "",
          tenant: item.dimension?.tenant,
        },
        unidades: translation.unidades ?? "",
        mapa: item.mapa,
      };
    });

    return indicadores;
  } catch (error) {
    console.error("Error fetching indicadores:", error);
  }
};

export const fetchLegend = async (
  indicatorId: number,
  unit?: string
): Promise<LegendFormatted | null> => {
  try {
    const me = await directus.request(readMe());
    const lang = (me?.title ?? "es").toLowerCase();

    const rows = await directus.request<LegendRaw[]>(
      readItems("leyendas", {
        filter: { indicador: { _eq: indicatorId } },
        sort: ["valor"],
        fields: ["color", "valor", "indicador", "translations.*"],
        limit: -1,
      })
    );

    if (!rows?.length) return null;

    const cuts = rows
      .filter((r) => typeof r.valor === "number")
      .map((r) => r.valor as number);

    const pickText = (r: LegendRaw) =>
      r.translations?.find((t) => t.lenguajes_code?.toLowerCase() === lang)
        ?.info ??
      r.translations?.[0]?.info ??
      "";

    if (rows.length === 1 && typeof rows[0].valor === "number") {
      const v = Number(rows[0].valor);
      const color = rows[0].color ?? "#ccc";
      const text = pickText(rows[0]);
      const labelsHtml = [
        `<div><span style="opacity:.85">${text}</span></div>`,
      ];
      return {
        breaks: [v, v],
        colors: [color],
        labelsHtml,
      };
    }

    if (cuts.length >= 3 && rows.length >= 4) {
      const [c1, c2, c3] = cuts;
      const breaks = [0, c1, c2, c3, Number.POSITIVE_INFINITY];
      const colors = [
        rows[3].color,
        rows[0].color,
        rows[1].color,
        rows[2].color,
      ];
      const texts = [
        pickText(rows[3]),
        pickText(rows[0]),
        pickText(rows[1]),
        pickText(rows[2]),
      ];
      const labelsHtml = [
        `<div><strong>&lt; ${fmt(c1)} ${unit}</strong><br/><span style="opacity:.85">${texts[0]}</span></div>`,
        `<div><strong>${fmt(c1)} – ${fmt(c2)} ${unit}</strong><br/><span style="opacity:.85">${texts[1]}</span></div>`,
        `<div><strong>${fmt(c2)} – ${fmt(c3)} ${unit}</strong><br/><span style="opacity:.85">${texts[2]}</span></div>`,
        `<div><strong>&gt; ${fmt(c3)} ${unit}</strong><br/><span style="opacity:.85">${texts[3]}</span></div>`,
      ];
      return { breaks, colors, labelsHtml };
    }

    return null;
  } catch (err) {
    console.error("fetchLegend error:", err);
    return null;
  }
};

